<script setup>
/**
 * The agenda, grouped by the month each session falls in.
 *
 * A month exists here only because a session put it here, so an empty month
 * cannot be rendered — there is nothing to render it from. The count in each
 * header is the length of that group rather than a separate tally, for the
 * same reason.
 *
 * One `openId` for the whole list is what makes "one card at a time" true:
 * there is only one value to hold, so opening a second closes the first
 * without any card knowing about any other.
 */
import { computed, onBeforeUnmount, ref } from 'vue';
import SessionCard from './SessionCard.vue';
import { monthKey, monthLabel, parseSessionDate } from '../../helpers/sessionCard';

const props = defineProps({
  sessions: { type: Array, default: () => [] },
  /**
   * Whether this viewer may edit and cancel; everyone here can open a room.
   * A function answers per session — a broker manages only the ones they made.
   */
  canManage: { type: [Boolean, Function], default: true },
  canEdit: { type: [Boolean, Function], default: false },
});

defineEmits(['join', 'edit', 'cancel']);

// One clock for every card, so "Starts in 8 min" counts down and a session
// that starts turns its way in into a way back without a reload.
const now = ref(new Date());
const tick = setInterval(() => { now.value = new Date(); }, 30_000);
onBeforeUnmount(() => clearInterval(tick));

const openId = ref(null);

const months = computed(() => {
  const groups = new Map();

  for (const session of props.sessions) {
    const key = monthKey(session);
    if (!groups.has(key)) groups.set(key, { key, label: monthLabel(session), sessions: [] });
    groups.get(key).sessions.push(session);
  }

  // The server already orders by start date, but a page whose order changed
  // upstream should still read as a calendar rather than as whatever arrived.
  return [...groups.values()]
    .sort((a, b) => a.key.localeCompare(b.key))
    .map((group) => ({
      ...group,
      sessions: [...group.sessions].sort(
        (a, b) => (parseSessionDate(a.start_date) ?? 0) - (parseSessionDate(b.start_date) ?? 0)
      ),
    }));
});

const countLabel = (count) => `${count} session${count === 1 ? '' : 's'}`;

const toggle = (session) => {
  openId.value = openId.value === session.id ? null : session.id;
};

// A card that closes on a page change would be one thing; a card that stays
// "open" while a different session takes its id would be another.
defineExpose({ collapse: () => { openId.value = null; } });
</script>

<template>
  <div class="session-months" data-testid="session-months">
    <section v-for="month in months" :key="month.key" :data-testid="`session-month-${month.key}`">
      <div class="session-months__head">
        <h2 class="xl-section-heading">{{ month.label }}</h2>
        <span class="session-months__rule" aria-hidden="true" />
        <span class="session-months__count">{{ countLabel(month.sessions.length) }}</span>
      </div>

      <SessionCard
        v-for="session in month.sessions"
        :key="session.id"
        :session="session"
        :open="openId === session.id"
        :can-manage="typeof props.canManage === 'function' ? props.canManage(session) : props.canManage"
        :can-edit="typeof props.canEdit === 'function' ? props.canEdit(session) : props.canEdit"
        :now="now"
        @toggle="toggle"
        @join="(s) => $emit('join', s)"
        @edit="(s) => $emit('edit', s)"
        @cancel="(s) => $emit('cancel', s)"
      />
    </section>

    <!-- What the cards are FOR, said once at the bottom: that a series can be
         changed one date at a time is not discoverable from a closed row. -->
    <p v-if="months.length" class="session-months__note">
      Click any session to see its details. Edit a repeating session to change one date, the whole series, or to skip a date.
    </p>
  </div>
</template>

<style scoped>
.session-months__head {
  display: flex;
  /* Baseline, not centre: the rule meets the month heading's own baseline
     rather than the middle of its line box. */
  align-items: baseline;
  gap: 12px;
  margin: 34px 0 14px;
}

/* Carries the eye from the month across to its count, as the board draws it. */
.session-months__rule {
  flex: 1;
  height: 1px;
  background: var(--xl-color-border);
}

.session-months__count {
  font-family: var(--xl-font-family);
  font-size: 12.5px;
  color: var(--xl-color-outline);
}

.session-months__note {
  margin: 16px 0 0;
  font-family: var(--xl-font-family);
  font-size: 13px;
  color: var(--xl-color-on-surface-variant);
}
</style>
